import { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle2, ExternalLink, Eye, EyeOff, Save, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Credential {
  key: string;
  source: 'override' | 'platform' | 'none';
  masked_value: string | null;
  is_set: boolean;
}

interface SettingsResponse {
  settings: Record<string, unknown>;
}

interface CredentialsResponse {
  credentials: Credential[];
}

/**
 * Admin → Settings → Security
 *
 * Currently configures Cloudflare Turnstile (site key + secret) for this
 * tenant. Site key is stored in site_settings (public, safe to expose);
 * secret is stored in credential_overrides (sensitive, masked display).
 *
 * Empty-state banner reminds operators that with no Turnstile configured,
 * the only bot defense in front of login/signup is the honeypot + signed
 * timestamp fallback shipped in PR #112.
 */
export default function SecuritySettings() {
  const [loading, setLoading] = useState(true);
  const [siteKey, setSiteKey] = useState('');
  const [siteKeyDraft, setSiteKeyDraft] = useState('');
  const [secretStatus, setSecretStatus] = useState<'unset' | 'set'>('unset');
  const [secretMasked, setSecretMasked] = useState<string | null>(null);
  const [secretDraft, setSecretDraft] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState<'site' | 'secret' | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, credsRes] = await Promise.all([
        api.get<SettingsResponse>('/admin/settings'),
        api.get<CredentialsResponse>('/admin/credentials'),
      ]);
      const ts = (settingsRes.settings?.turnstile_site_key as string) || '';
      setSiteKey(ts);
      setSiteKeyDraft(ts);

      const secretRow = (credsRes.credentials || []).find((c) => c.key === 'TURNSTILE_SECRET_KEY');
      if (secretRow?.is_set) {
        setSecretStatus('set');
        setSecretMasked(secretRow.masked_value);
      } else {
        setSecretStatus('unset');
        setSecretMasked(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load Turnstile settings');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function saveSiteKey() {
    setSaving('site');
    setError('');
    try {
      await api.put('/admin/settings', { settings: { turnstile_site_key: siteKeyDraft.trim() } });
      setSiteKey(siteKeyDraft.trim());
      setSuccessMsg('Site key saved');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err: any) {
      setError(err?.message || 'Failed to save site key');
    } finally {
      setSaving(null);
    }
  }

  async function saveSecret() {
    if (!secretDraft.trim()) return;
    setSaving('secret');
    setError('');
    try {
      await api.put('/admin/credentials/TURNSTILE_SECRET_KEY', { value: secretDraft.trim() });
      setSecretDraft('');
      setShowSecret(false);
      setSuccessMsg('Secret key saved');
      setTimeout(() => setSuccessMsg(''), 2500);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to save secret key');
    } finally {
      setSaving(null);
    }
  }

  async function clearSecret() {
    if (!confirm('Remove the saved Turnstile secret? Login/signup will fall back to the honeypot floor only.')) return;
    setSaving('secret');
    setError('');
    try {
      await api.delete('/admin/credentials/TURNSTILE_SECRET_KEY');
      setSuccessMsg('Secret key cleared');
      setTimeout(() => setSuccessMsg(''), 2500);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to clear secret key');
    } finally {
      setSaving(null);
    }
  }

  const turnstileConfigured = !!siteKey && secretStatus === 'set';

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-stone-700" aria-hidden="true" />
        <h1 className="text-2xl font-semibold text-stone-900">Security</h1>
      </div>

      {/* Empty-state warning */}
      {!turnstileConfigured && (
        <div
          role="alert"
          className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium">Turnstile not configured</p>
            <p className="mt-1">
              Login and signup forms are protected by a honeypot + time-window
              floor (always-on, free). For stronger bot resistance, add a
              Cloudflare Turnstile widget below.
            </p>
          </div>
        </div>
      )}

      {turnstileConfigured && (
        <div className="flex gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium">Turnstile is configured</p>
            <p className="mt-1">
              Your auth forms verify with Cloudflare before submitting, and the
              honeypot fallback is layered underneath as defense-in-depth.
            </p>
          </div>
        </div>
      )}

      {successMsg && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {successMsg}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm text-rose-800"
        >
          {error}
        </div>
      )}

      <Card>
        <div className="space-y-6 p-6">
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Cloudflare Turnstile</h2>
            <p className="mt-1 text-sm text-stone-600">
              Tenant-managed bot protection for login, signup, and password
              reset. Bring your own Cloudflare account and widget — Rumio
              never sees the verification traffic.
            </p>
          </div>

          {/* How to get this */}
          <details className="rounded-lg border border-stone-200 bg-stone-50 p-4">
            <summary className="cursor-pointer font-medium text-stone-900">
              How do I get a site key and secret key?
            </summary>
            <ol className="ml-5 mt-3 list-decimal space-y-2 text-sm text-stone-700">
              <li>
                Sign up (free) at{' '}
                <a
                  href="https://dash.cloudflare.com/sign-up"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-blue-700 underline"
                >
                  cloudflare.com
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
                .
              </li>
              <li>
                In the dashboard, go to <strong>Turnstile</strong> in the left
                sidebar.
              </li>
              <li>
                Click <strong>Add widget</strong>. Name it after your site, add
                your domain(s), and pick <strong>Managed</strong> mode.
              </li>
              <li>
                Cloudflare gives you a <strong>Site Key</strong> (starts with
                <code className="mx-1 rounded bg-stone-200 px-1 py-0.5 text-xs">0x4AAAA…</code>)
                and a <strong>Secret Key</strong> (starts with
                <code className="mx-1 rounded bg-stone-200 px-1 py-0.5 text-xs">0x4AAAA…</code>
                too — the dashboard distinguishes which is which).
              </li>
              <li>Paste both below and click Save.</li>
            </ol>
            <p className="mt-3 text-xs text-stone-500">
              Cloudflare Turnstile is free up to 1M verifications/month. Your
              tenant's traffic is billed to your own account, not Rumio's.
            </p>
          </details>

          {/* Site key */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-stone-700" htmlFor="turnstile-site-key">
              Site Key
            </label>
            <div className="flex gap-2">
              <Input
                id="turnstile-site-key"
                type="text"
                value={siteKeyDraft}
                onChange={(e) => setSiteKeyDraft(e.target.value)}
                placeholder="0x4AAAAAA…"
                className="font-mono"
              />
              <Button
                onClick={saveSiteKey}
                isLoading={saving === 'site'}
                disabled={siteKeyDraft.trim() === siteKey}
                size="md"
              >
                <Save className="mr-1 h-4 w-4" aria-hidden="true" />
                Save
              </Button>
            </div>
            <p className="text-xs text-stone-500">
              Public — embedded in your site's HTML.
            </p>
          </div>

          {/* Secret key */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-stone-700" htmlFor="turnstile-secret-key">
              Secret Key
            </label>
            {secretStatus === 'set' && (
              <div className="flex items-center justify-between rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700">
                <span className="font-mono">{secretMasked}</span>
                <button
                  type="button"
                  onClick={clearSecret}
                  disabled={saving === 'secret'}
                  className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Remove
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="turnstile-secret-key"
                  type={showSecret ? 'text' : 'password'}
                  value={secretDraft}
                  onChange={(e) => setSecretDraft(e.target.value)}
                  placeholder={secretStatus === 'set' ? 'Enter a new key to rotate' : '0x4AAAAAA…'}
                  className="font-mono pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                onClick={saveSecret}
                isLoading={saving === 'secret'}
                disabled={!secretDraft.trim()}
                size="md"
              >
                <Save className="mr-1 h-4 w-4" aria-hidden="true" />
                Save
              </Button>
            </div>
            <p className="text-xs text-stone-500">
              Sensitive — stored encrypted at rest, never exposed to the
              frontend. Rumio uses this to verify Cloudflare's challenge tokens.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
